// A cited block's outline on its page, normalized to [0, 1] from the top-left corner, as olio-api
// stores it on a citation's `highlights`. Lives with the models so a model can parse it without
// importing a component.
// `tablePart` marks a cited table: `whole` for a table on one page, or where one page's part sits in a
// table that continues across pages (olio-ai's `CitationRegion.table_part`).
const TABLE_PARTS = ['whole', 'first', 'middle', 'last'] as const;
export type CitationTablePart = (typeof TABLE_PARTS)[number];

// The edges of a table part that continue onto another page: drawn open, and what joins the parts into
// one piece of evidence.
export const TABLE_PART_OPEN_EDGES: Record<CitationTablePart, { top: boolean; bottom: boolean }> = {
  whole: { top: false, bottom: false },
  first: { top: false, bottom: true },
  middle: { top: true, bottom: true },
  last: { top: true, bottom: false },
};
export type CitationHighlight = {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  tablePart?: CitationTablePart;
};

const isPage = (value: unknown): value is number => Number.isInteger(value) && (value as number) > 0;
const isUnit = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;

// Stored JSON is only as good as whatever wrote it — a value from before the api validated these, or
// one patched by hand — so both are re-checked where they are read rather than cast. Undefined when
// absent, which readers already treat as "open at the document's first page, nothing outlined".
export function parseCitationPages(value: unknown): number[] | undefined {
  return Array.isArray(value) ? value.filter(isPage) : undefined;
}

export function parseCitationHighlights(value: unknown): CitationHighlight[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.flatMap((h) => {
    if (
      typeof h !== 'object' ||
      h === null ||
      !isPage(h.page) ||
      !isUnit(h.x) ||
      !isUnit(h.y) ||
      !isUnit(h.w) ||
      !isUnit(h.h) ||
      h.w <= 0 ||
      h.h <= 0
    )
      return [];
    const box: CitationHighlight = { page: h.page, x: h.x, y: h.y, w: h.w, h: h.h };
    const tablePart = h.tablePart ?? h.table_part;
    return [isTablePart(tablePart) ? { ...box, tablePart } : box];
  });
}

const isTablePart = (value: unknown): value is CitationTablePart => (TABLE_PARTS as readonly unknown[]).includes(value);

// The spots a reader steps through: each box on its own, except a table that continues across pages,
// whose parts arrive together in page order and are one spot.
export function citationSpots(highlights: CitationHighlight[]): CitationHighlight[][] {
  const spots: CitationHighlight[][] = [];
  highlights.forEach((h) => {
    const spot = spots[spots.length - 1];
    const previous = spot?.[spot.length - 1];
    const continues =
      previous?.tablePart &&
      h.tablePart &&
      TABLE_PART_OPEN_EDGES[previous.tablePart].bottom &&
      TABLE_PART_OPEN_EDGES[h.tablePart].top &&
      h.page > previous.page;
    if (spot && continues) spot.push(h);
    else spots.push([h]);
  });
  return spots;
}
